import {useState} from 'react';
import {useCart} from '@shopify/hydrogen';
import {useEffect} from 'react';
import {PropTypes} from 'prop-types';

import {CART_COOKIE} from '~/consts/cookies';
import {getCookie, setCookie} from '~/utils/cookies';

/**
 * A client component to encapsulate client-side effects to change various things with brand subdomain.
 */
export function DynamicBrands({faviconSrc}) {
  const windowReady = typeof window !== 'undefined';
  const {id: thisSubdomainCart} = useCart();
  const [otherSubdomainCart, setOtherSubdomainCart] = useState();

  // Cart persistence effects - manage shared subdomain cookie +
  // tweak local storage for Shopify hook
  useEffect(() => {
    if (!windowReady) return;

    setOtherSubdomainCart(getCookie(CART_COOKIE));
  }, [windowReady]);

  useEffect(() => {
    if (!windowReady) return;

    if (otherSubdomainCart && !thisSubdomainCart) {
      // Existing cart from another subdomain takes precedence over no cart here:
      localStorage.setItem('shopifyCartId', otherSubdomainCart);
    } else if (thisSubdomainCart && !otherSubdomainCart) {
      // In the opposite case, make sure the parent domain has a cookie to reference this cart:
      setCookie(CART_COOKIE, thisSubdomainCart, 1);
    } else if (
      (thisSubdomainCart ?? undefined) !== (otherSubdomainCart ?? undefined)
    ) {
      // Finally, if both exist but are not equal (unlikely in built version
      // except perhaps post-checkout), let's defer to here, just because
      // the user may be in the middle of something, or the old one is stale due to a checkout:
      setCookie(CART_COOKIE, thisSubdomainCart, 1);
      localStorage.setItem('shopifyCartId', thisSubdomainCart);
    }
    // ... and do nothing if there are neither, or if they are equal
  }, [thisSubdomainCart, otherSubdomainCart, windowReady]);

  // Favicon effect - keep it in sync with the current brand
  useEffect(() => {
    if (!windowReady) return;

    let link = window.document.querySelector("link[rel~='icon']");

    // Do not want to keep running if we would be replacing with the same icon - leads to
    // an extra UI blip
    if (faviconSrc?.length && link?.href !== faviconSrc) {
      if (!link) {
        link = window.document.createElement('link');
        link.rel = 'icon';
        window.document.getElementsByTagName('head')[0].appendChild(link);
      }

      link.href = faviconSrc;
    }
  }, [faviconSrc, windowReady]);

  return null;
}

DynamicBrands.propTypes = {
  faviconSrc: PropTypes.string,
};
